import { throttle } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { BsEye } from 'react-icons/bs'
import { Label, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import styles from './EEGAnalytics.module.css'
import eegData from './eeg_short_dementia_flags.json'
import UserSelector from '../components/UserSelector'
import Activity from '../components/Activity'

const EEGAnalytics = () => {
  const [data, setData] = useState([])
  const [activePoint, setActivePoint] = useState(null) // Data point for the hovered timestamp
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 }) // Global mouse coordinates for tooltip
  const [anomalies, setAnomalies] = useState([]) // Detected anomaly ranges
  const [visibleChannels, setVisibleChannels] = useState([]) // Channels currently selected to be displayed
  const [hoveredChannel, setHoveredChannel] = useState(null)
  const [isPopoverVisible, setIsPopoverVisible] = useState(false)

  const tooltipRef = useRef(null) // Ref for the tooltip div to measure its dimensions

  // EEG channel names (standard 10-20 system)
  const allChannels = ['F3', 'F4', 'F7', 'F8', 'T7', 'T8', 'C3', 'C4', 'P3', 'P4', 'O1', 'O2', 'Fp1', 'Fp2']

  // Initialize all channels as visible on the first render
  useEffect(() => {
    setVisibleChannels(allChannels) // Start with all channels visible
  }, [])

  // Replace the sample data generation with actual data loading
  useEffect(() => {
    const processEEGData = () => {
      // Get the start time from the first data point
      const startTime = new Date(eegData.eeg_data[0].timestamp).getTime()
      const tenSecondsInMs = 10 * 1000 // 10 seconds in milliseconds
      const endTime = startTime + tenSecondsInMs

      // Filter data for first 10 seconds
      const processedData = eegData.eeg_data.map((point) => ({
        timestamp: new Date(point.timestamp).getTime(),
        ...point.channels,
      }))

      // Filter dementia flags for first 10 seconds
      const dementiaByChannel = {}
      Object.entries(eegData.dementia_flags).forEach(([channel, periods]) => {
        dementiaByChannel[channel] = periods.map((period) => ({
          start: new Date(period.start).getTime(),
          end: new Date(period.end).getTime(),
        }))
      })

      setData(processedData)
      setAnomalies(dementiaByChannel)
    }

    processEEGData()
  }, [])

  // Effect to capture global mouse movement for accurate tooltip positioning
  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePosition({ x: event.clientX, y: event.clientY })
    }

    window.addEventListener('mousemove', handleMouseMove)

    // Cleanup function to remove the event listener on component unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, []) // Empty dependency array ensures this runs once

  // Update X-axis formatter to show seconds with milliseconds
  const formatTimestamp = (timestamp) => {
    const relativeTime = (timestamp - data[0]?.timestamp) / 1000 // Convert to seconds
    return `${relativeTime.toFixed(2)}s`
  }

  // Update channel colors to darker, more saturated tones
  const getChannelColor = (channelName) => {
    const colors = [
      '#D32F2F', // Deep red
      '#0277BD', // Strong blue
      '#2E7D32', // Forest green
      '#5E35B1', // Deep purple
      '#EF6C00', // Dark orange
      '#00695C', // Dark teal
      '#6D4C41', // Brown
      '#455A64', // Blue grey
      '#4527A0', // Deep violet
      '#00838F', // Dark cyan
      '#283593', // Indigo
      '#2E7C31', // Dark green
      '#C62828', // Dark red
      '#4A148C', // Purple
    ]
    const originalIndex = allChannels.indexOf(channelName)
    return colors[originalIndex % colors.length]
  }

  // Calculates the tooltip's position to prevent it from going off-screen
  const getTooltipStyle = () => {
    const currentTooltip = tooltipRef.current
    // Get actual tooltip dimensions, or use defaults if not yet rendered
    const tooltipWidth = currentTooltip ? currentTooltip.offsetWidth : 250
    const tooltipHeight = currentTooltip ? currentTooltip.offsetHeight : 160 // Approximate height, adjust if content varies greatly
    const offset = 15 // Padding between mouse and tooltip

    let left = mousePosition.x + offset
    let top = mousePosition.y + offset

    // Flip horizontally if clipping right, else keep right of cursor
    left = mousePosition.x + tooltipWidth + offset > window.innerWidth ? mousePosition.x - tooltipWidth - offset : mousePosition.x + offset
    left = Math.max(0, left)

    // Flip vertically if clipping bottom, else keep below cursor
    top = mousePosition.y + tooltipHeight + offset > window.innerHeight ? mousePosition.y - tooltipHeight - offset : mousePosition.y + offset
    top = Math.max(0, top)

    return { left, top }
  }

  // Toggles the visibility of a channel
  const handleChannelToggle = (channel) => {
    setVisibleChannels((prevVisibleChannels) => {
      if (prevVisibleChannels.includes(channel)) {
        return prevVisibleChannels.filter((c) => c !== channel)
      } else {
        return [...prevVisibleChannels, channel]
      }
    })
  }

  // Toggle all channels on or off
  const handleToggleAll = () => {
    setVisibleChannels((prev) => (prev.length === allChannels.length ? [] : [...allChannels]))
  }

  // Determines if only one channel is currently visible for Y-axis display
  const singleVisibleChannel = visibleChannels.length === 1 ? visibleChannels[0] : null

  // Memoize the channel colors to prevent recalculation
  const channelColors = useMemo(() => {
    return allChannels.reduce((acc, channel) => {
      acc[channel] = getChannelColor(channel)
      return acc
    }, {})
  }, [])

  // Channel selection popover using React-Bootstrap
  const channelPopover = (
    <Popover id='channel-popover' className={styles.popover}>
      <div className={styles.popoverHeader}>
        <div className={styles.headerContent}>
          <h3 className={styles.popoverTitle}>Channels</h3>
          <button className={styles.toggleButton} onClick={handleToggleAll}>
            {visibleChannels.length === allChannels.length ? 'Hide All' : 'Show All'}
          </button>
        </div>
      </div>
      <div className={styles.popoverBody}>
        <div className={styles.channelGrid}>
          {allChannels.map((channel) => (
            <div key={channel} className={styles.channelItem} onClick={() => handleChannelToggle(channel)}>
              <input type='checkbox' className={styles.channelCheckbox} checked={visibleChannels.includes(channel)} onChange={() => {}} />
              <span>{channel}</span>
            </div>
          ))}
        </div>
      </div>
    </Popover>
  )

  // Throttle mouse move handler
  const handleMouseMove = useCallback(
    throttle((state, channel) => {
      if (state.activePayload?.[0] && !isPopoverVisible) {
        setActivePoint(state.activePayload[0].payload)
        if (state.activeLabel) {
          setHoveredChannel(channel)
        }
      }
    }, 16),
    [isPopoverVisible]
  )

  // Memoize chart rendering
  const renderCharts = useMemo(() => {
    if (!visibleChannels.length) return null

    return visibleChannels.map((channel, index) => (
      <div
        key={channel}
        className={styles.chartWrapper}
        style={{
          // Add extra height to last chart to compensate for X-axis
          flex: index === visibleChannels.length - 1 || visibleChannels.length === 1 ? '1.2' : '1',
        }}
      >
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={data}
            margin={{ top: 5, right: 40, left: 5, bottom: 5 }}
            syncId='eeg-sync'
            onMouseMove={(state) => handleMouseMove(state, channel)}
            onMouseLeave={() => {
              setActivePoint(null)
              setHoveredChannel(null)
            }}
          >
            <XAxis
              dataKey='timestamp'
              type='number'
              scale='time'
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatTimestamp}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#1F2937' }} // Updated to darker color
              ticks={data.map((item) => item.timestamp).filter((_, i) => i % 2 === 0)} // Show every other tick
              height={20}
              hide={!(visibleChannels.indexOf(channel) === visibleChannels.length - 1 || visibleChannels.length === 1)}
            />
            {/* Conditional Y-Axis: visible only if this is the only channel displayed */}
            {singleVisibleChannel === channel && (
              <YAxis
                dataKey={channel}
                orientation='left'
                stroke='#1F2937' // Updated to darker color
                tick={{ fontSize: 10, fill: '#1F2937' }} // Updated to darker color
                tickFormatter={(value) => `${Math.round(value)}μV`}
                domain={[-100, 100]}
                ticks={[-100, -75, -50, -25, 0, 25, 50, 75, 100]} // Fixed ticks at whole numbers
                width={40} // Increased width to accommodate larger numbers
                allowDataOverflow={true} // Prevent data from extending beyond domain
                hide={false}
              />
            )}
            {/* Reference line at zero microvolts */}
            <ReferenceLine y={0} stroke='#fff'>
              <Label
                value={channel}
                position='right'
                offset={15}
                fontSize={10}
                fontWeight={700}
                fill='#1F2937' // Updated from white to dark
              />
            </ReferenceLine>
            {/* Dynamic vertical crosshair line based on active mouse position */}
            {activePoint && (
              <ReferenceLine
                x={activePoint.timestamp}
                stroke='#6B7280' // Updated from white to medium gray
                strokeWidth={1}
                strokeDasharray='3 3' // Dashed line
              />
            )}

            {anomalies[channel]?.map((period, i) => (
              <ReferenceArea
                key={`dementia-${i}`}
                x1={period.start}
                x2={period.end}
                stroke='#FFD600' // Bright yellow stroke
                fill='#FFD600' // Bright yellow fill
                fillOpacity={0.3}
                onMouseEnter={(e) => {
                  setDementiaHover({
                    visible: true,
                    x: e.pageX,
                    y: e.pageY,
                  })
                }}
                onMouseLeave={() => setDementiaHover({ visible: false, x: 0, y: 0 })}
              >
                {singleVisibleChannel && (
                  <Label
                    value={`Dementia Period ${i + 1}`}
                    position='insideTop'
                    offset={60}
                    angle={270}
                    fill='#000' // Bright yellow text
                    fontSize={12} // Slightly larger font
                    fontWeight='bold'
                  />
                )}
              </ReferenceArea>
            ))}
            <Line
              type='linear'
              dataKey={channel}
              stroke={channelColors[channel]}
              strokeWidth={1}
              dot={false} // Hide data points for a cleaner line
              isAnimationActive={false} // Disable animation for smoother performance
              // Add onMouseEnter and onMouseLeave to detect hover on this specific line
              onMouseEnter={() => setHoveredChannel(channel)}
              onMouseLeave={() => setHoveredChannel(null)}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    ))
  }, [visibleChannels, data, channelColors, handleMouseMove, activePoint])

  // Add new state for dementia tooltip
  const [dementiaHover, setDementiaHover] = useState({
    visible: false,
    x: 0,
    y: 0,
  })

  return (
    <div>
      <div className='dashboard-heading'>
        <div className='content-container m-0'>
          <h2>EEG Analytics</h2>
          <p className='m-0'>View EEG data and analysis results.</p>
        </div>
        <Activity />
      </div>

      <div className='d-flex justify-content-between align-items-center'>
        <UserSelector />
        <div className={styles.channelSelector}>
          <OverlayTrigger trigger='click' placement='bottom-end' overlay={channelPopover} rootClose onToggle={(show) => setIsPopoverVisible(show)}>
            <button className={styles.eyeButton}>
              <BsEye size={20} />
            </button>
          </OverlayTrigger>
        </div>
      </div>
      <div className='dashboard-cards p-2'>
        <div className={styles.container}>
          {/* Charts Display Area - uses flex-grow and flex-col to distribute height */}
          <div className={styles.chartsContainer}>
            {visibleChannels.length > 0 ? (
              renderCharts
            ) : (
              // Message displayed when no channels are selected
              <div className={styles.noChannelsMessage}>No channels selected. Please select channels from the "Show Channels" menu.</div>
            )}
          </div>

          {/* Original tooltip - now hidden */}
          {false && activePoint && !isPopoverVisible && (
            <div
              ref={tooltipRef} // Attach ref to get actual tooltip dimensions for positioning
              className={styles.tooltip}
              style={{
                ...getTooltipStyle(), // Dynamically calculated position (flips if clipping)
                pointerEvents: 'none', // Ensures the tooltip doesn't block mouse events on charts
              }}
            >
              <p className={styles.tooltipHeader}>Time: {formatTimestamp(activePoint.timestamp)}</p>
              <div className={styles.tooltipGrid}>
                {/* Iterate ONLY over visible channels for the tooltip content */}
                {visibleChannels.map(
                  (channel) =>
                    activePoint[channel] !== undefined && ( // Only display if data exists for that channel at the active point
                      <div
                        key={channel}
                        className={`
                      ${styles.tooltipItem}
                      ${hoveredChannel === channel ? styles.tooltipItemActive : ''}
                    `}
                      >
                        <div className={styles.channelDot} style={{ backgroundColor: getChannelColor(channel) }}></div>
                        <span>
                          {channel}: {activePoint[channel] ? activePoint[channel].toFixed(2) : 'N/A'}
                          μV
                        </span>
                      </div>
                    )
                )}
              </div>
            </div>
          )}

          {/* New dementia tooltip */}
          {dementiaHover.visible && (
            <div
              ref={tooltipRef}
              className={styles.tooltip}
              style={{
                ...getTooltipStyle(),
                pointerEvents: 'none',
                background: 'rgba(55, 65, 81)',
                padding: '8px 12px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                borderRadius: '6px',
                border: 'none',
              }}
            >
              Possible Dementia Marker
            </div>
          )}

          {/* Footer / Information area */}
        </div>
      </div>
    </div>
  )
}

export default EEGAnalytics
